import { Alert } from '@/api'
import { AddressCodeDialogInitialFocus } from '@/types'

export interface KanaBibliography {
  display: string
  kana: string
}

export interface Data {
  customer: {
    code: string
  }
  alerts: Alert[]
  tooli: {
    enabled: boolean
  }
  receiptInfo: {
    lend: {
      bulkPrint: boolean
    }
    lendStatus: {
      title: string
      branchNamePrefix: string
      displayAuthor: string
      displayReserveCount: string
      dueDateLabel: string
      userNoMaskDigit: number
      collectionPartitionCount: number
      abbreviationUnitLabel: string
      totalLabel: string
      totalUnitLabel: string
      totalLabelFormatType: string
      notices: string[]
      displayLostCollection: string
    }
    reserveStatus: {
      userNoMaskDigit: number
    }
    groupLendOutputPrinter: boolean
  }
  kana: {
    bibliographies: KanaBibliography[]
  }
  icTag: {
    enabled: boolean
    uidEnabled: boolean
    industryCode: string
    branchType: string
    agencyCode: string
    agencyCodeCheck: string
    acceptCollectionActualAfi: string
    acceptCollectionMaintenanceIcWrite: string
    acceptCollectionMaintenanceIcRead: string
  }
  app: {
    enableManagementInfo: boolean
    barcodeReaderSoundEnabled: boolean
    schoolEnabled: boolean
    requestReserveEnabled: boolean
  }
  page: {
    lend: {
      displayWorkType: string
      displayGender: string
      displayUserName: string
      insideReadingHeaderBarColor: string
      displayReceiptOutputInsideReading: string
      lendExtendReceiptOutput: string
      displayReadingInside: string
    }
    return: {
      readingCountDisplayType: string
      userIdButton: string
      displayReturnBorrow: string
      userIdButtonUserDisplayType: string
    }
    user: {
      displayWorkType: string
      resetCardLostCountOnExpirationUpdate: string
      displayCardLostCountUpdateAlertOnReissue: string
      basicTab: {
        displayMyBranch: string
        displayReserveReceivePlace: string
        displayReserveContactMethod: string
        displayOpacSetting: string
        displayOther: string
        displayAddress: string
        displayMailAddress: string
      }
      otherContactTab: {
        display: string
      }
      newArrivalNoticeTab: {
        display: string
      }
      oldNameTab: {
        display: string
      }
      usageSituationTab: {
        displayRemindCountHistory: string
        displayReserveCountHistory: string
      }
      externalCollaborationTab: {
        display: string
      }
    }
    userSearch: {
      displayAdditionalSearch: string
      displayPhone: string
      displayAddress: string
      displayMailAddress: string
    }
    shelfTypeTempChange: {
      displayHandlingType: string
    }
    shelfTypeTempChangeFix: {
      displayHandlingType: string
    }
    reserve: {
      displayReserveUserName: string
      displayReserveMethod: string
      displayForwardingBranch: string
      displayForwardingPlace: string
      displayReserveContactMethod: string
    }
    bibliographyDetail: {
      tooliDownload: {
        defaultMaterialFormat: {
          bibliographyReal: string
          bibliographyContent: string
        }
      }
      displayReserveUserName: string
      collectionOrderTab: {
        displayInterLibraryLoanCollectionRegister: string
        displayAccept: string
        displayRegister: string
        displayOrder: string
        displayRemovedSwitchDefaultValue: string
        displaySetNumber: string
      }
      displayBibliographyIntegration: string
      contentTab: {
        display: string
        requiredType: string
      }
      bibliographyTab: {
        displayCopyRegister: string
        displayEdit: string
      }
    }
    bibliographySearch: {
      displayInterLibraryLoanCollectionCreate: string
      alone: {
        initialSelect: string
      }
    }
    bibliographySearchResult: {
      displayInterLibraryLoanCollectionRegister: string
      displayInterLibraryLoanCollectionCreate: string
    }
    systemCodeDialog: {
      initialFocusPosition: string
    }
    addressCodeDialog: {
      initialFocusPosition: AddressCodeDialogInitialFocus
    }
  }
  autoLib: {
    enabled: boolean
    closedShelfTypes: string[]
    retrievalStationCode: string
  }
  rewritecard: {
    enabled: boolean
  }
  mynumbercard: {
    enabled: boolean
  }
  batch: {
    trcBellNewOrderRegistration: boolean
    reminderList: {
      outputOptionDisabled: boolean
    }
  }
  felica: {
    enabled: boolean
  }
  env: {
    headerBarColor: string
  }
}

export const defaultData = (): Data => ({
  customer: {
    code: ''
  },
  alerts: [],
  tooli: {
    enabled: false
  },
  receiptInfo: {
    lend: {
      bulkPrint: false
    },
    lendStatus: {
      title: '',
      branchNamePrefix: '',
      displayAuthor: '0',
      displayReserveCount: '1',
      dueDateLabel: '',
      userNoMaskDigit: 0,
      collectionPartitionCount: 0,
      abbreviationUnitLabel: '',
      totalLabel: '',
      totalUnitLabel: '',
      totalLabelFormatType: '',
      notices: [],
      displayLostCollection: '1'
    },
    reserveStatus: {
      userNoMaskDigit: 0
    },
    groupLendOutputPrinter: false
  },
  kana: {
    bibliographies: []
  },
  icTag: {
    enabled: false,
    uidEnabled: false,
    industryCode: '',
    branchType: '',
    agencyCode: '',
    agencyCodeCheck: '0',
    acceptCollectionActualAfi: '0',
    acceptCollectionMaintenanceIcWrite: '0',
    acceptCollectionMaintenanceIcRead: '0'
  },
  app: {
    enableManagementInfo: false,
    barcodeReaderSoundEnabled: false,
    schoolEnabled: false,
    requestReserveEnabled: false
  },
  page: {
    lend: {
      displayWorkType: '0',
      displayGender: '0',
      displayUserName: '1',
      insideReadingHeaderBarColor: '#4caf50',
      displayReceiptOutputInsideReading: '0',
      lendExtendReceiptOutput: '0',
      displayReadingInside: '1'
    },
    return: {
      readingCountDisplayType: '0',
      userIdButton: '0',
      displayReturnBorrow: '1',
      userIdButtonUserDisplayType: '0'
    },
    user: {
      displayWorkType: '0',
      resetCardLostCountOnExpirationUpdate: '0',
      displayCardLostCountUpdateAlertOnReissue: '0',
      basicTab: {
        displayMyBranch: '1',
        displayReserveReceivePlace: '1',
        displayReserveContactMethod: '1',
        displayOpacSetting: '1',
        displayOther: '1',
        displayAddress: '1',
        displayMailAddress: '1'
      },
      otherContactTab: {
        display: '1'
      },
      newArrivalNoticeTab: {
        display: '1'
      },
      oldNameTab: {
        display: '1'
      },
      usageSituationTab: {
        displayRemindCountHistory: '1',
        displayReserveCountHistory: '1'
      },
      externalCollaborationTab: {
        display: '0'
      }
    },
    userSearch: {
      displayAdditionalSearch: '0',
      displayPhone: '1',
      displayAddress: '1',
      displayMailAddress: '1'
    },
    shelfTypeTempChange: {
      displayHandlingType: '0'
    },
    shelfTypeTempChangeFix: {
      displayHandlingType: '0'
    },
    reserve: {
      displayReserveUserName: '0',
      displayReserveMethod: '1',
      displayForwardingBranch: '1',
      displayForwardingPlace: '1',
      displayReserveContactMethod: '1'
    },
    bibliographyDetail: {
      tooliDownload: {
        defaultMaterialFormat: {
          bibliographyReal: '0',
          bibliographyContent: '0'
        }
      },
      displayReserveUserName: '0',
      collectionOrderTab: {
        displayInterLibraryLoanCollectionRegister: '1',
        displayAccept: '1',
        displayRegister: '1',
        displayOrder: '1',
        displayRemovedSwitchDefaultValue: '1',
        displaySetNumber: '0'
      },
      displayBibliographyIntegration: '1',
      contentTab: {
        display: '1',
        requiredType: '1'
      },
      bibliographyTab: {
        displayCopyRegister: '1',
        displayEdit: '1'
      }
    },
    bibliographySearch: {
      displayInterLibraryLoanCollectionCreate: '1',
      alone: {
        initialSelect: '1'
      }
    },
    bibliographySearchResult: {
      displayInterLibraryLoanCollectionRegister: '1',
      displayInterLibraryLoanCollectionCreate: '1'
    },
    systemCodeDialog: {
      initialFocusPosition: '0'
    },
    addressCodeDialog: {
      initialFocusPosition: AddressCodeDialogInitialFocus.AddressCode
    }
  },
  autoLib: {
    enabled: false,
    closedShelfTypes: [],
    retrievalStationCode: ''
  },
  rewritecard: {
    enabled: false
  },
  mynumbercard: {
    enabled: false
  },
  batch: {
    trcBellNewOrderRegistration: false,
    reminderList: {
      outputOptionDisabled: false
    }
  },
  felica: {
    enabled: false
  },
  env: {
    headerBarColor: '#4769cb' // デフォルト値はこれまでの色「primary」
  }
})

export function DataFromJSON(json: any): Data {
  return {
    customer: {
      code: json.customer.code
    },
    alerts: json.alerts,
    tooli: {
      enabled: json.tooli.enabled
    },
    receiptInfo: {
      lend: {
        bulkPrint: json.receipt_info.lend.bulk_print
      },
      lendStatus: {
        title: json.receipt_info.lend_status.title,
        branchNamePrefix: json.receipt_info.lend_status.branch_name_prefix,
        displayAuthor: json.receipt_info.lend_status.display_author,
        displayReserveCount:
          json.receipt_info.lend_status.display_reserve_count,
        dueDateLabel: json.receipt_info.lend_status.due_date_label,
        userNoMaskDigit: json.receipt_info.lend_status.user_no_mask_digit,
        collectionPartitionCount:
          json.receipt_info.lend_status.collection_partition_count,
        abbreviationUnitLabel:
          json.receipt_info.lend_status.abbreviation_unit_label,
        totalLabel: json.receipt_info.lend_status.total_label,
        totalUnitLabel: json.receipt_info.lend_status.total_unit_label,
        totalLabelFormatType:
          json.receipt_info.lend_status.total_label_format_type,
        notices: json.receipt_info.lend_status.notices,
        displayLostCollection:
          json.receipt_info.lend_status.display_lost_collection
      },
      reserveStatus: {
        userNoMaskDigit: json.receipt_info.reserve_status.user_no_mask_digit
      },
      groupLendOutputPrinter: json.receipt_info.group_lend_output_printer
    },
    kana: {
      bibliographies: json.kana.bibliographies
    },
    icTag: {
      enabled: json.ic_tag.enabled,
      uidEnabled: json.ic_tag.uid_enabled,
      industryCode: json.ic_tag.industry_code,
      branchType: json.ic_tag.branch_type,
      agencyCode: json.ic_tag.agency_code,
      agencyCodeCheck: json.ic_tag.agency_code_check,
      acceptCollectionActualAfi: json.ic_tag.accept_collection_actual_afi,
      acceptCollectionMaintenanceIcWrite:
        json.ic_tag.accept_collection_maintenance_ic_write,
      acceptCollectionMaintenanceIcRead:
        json.ic_tag.accept_collection_maintenance_ic_read
    },
    app: {
      enableManagementInfo: json.app.enable_management_info,
      barcodeReaderSoundEnabled: json.app.barcode_reader_sound_enabled,
      schoolEnabled: json.app.school_enabled,
      requestReserveEnabled: json.app.request_reserve_enabled
    },
    page: {
      lend: {
        displayWorkType: json.page.lend.display_work_type,
        displayGender: json.page.lend.display_gender,
        displayUserName: json.page.lend.display_user_name,
        insideReadingHeaderBarColor:
          json.page.lend.inside_reading_header_bar_color,
        displayReceiptOutputInsideReading:
          json.page.lend.display_receipt_output_inside_reading,
        lendExtendReceiptOutput: json.page.lend.lend_extend_receipt_output,
        displayReadingInside: json.page.lend.display_reading_inside
      },
      return: {
        readingCountDisplayType: json.page.return.reading_count_display_type,
        userIdButton: json.page.return.user_id_button,
        displayReturnBorrow: json.page.return.display_return_borrow,
        userIdButtonUserDisplayType:
          json.page.return.user_id_button_user_display_type
      },
      user: {
        displayWorkType: json.page.user.display_work_type,
        resetCardLostCountOnExpirationUpdate:
          json.page.user.reset_card_lost_count_on_expiration_update,
        displayCardLostCountUpdateAlertOnReissue:
          json.page.user.display_card_lost_count_update_alert_on_reissue,
        basicTab: {
          displayMyBranch: json.page.user.basic_tab.display_my_branch,
          displayReserveReceivePlace:
            json.page.user.basic_tab.display_reserve_receive_place,
          displayReserveContactMethod:
            json.page.user.basic_tab.display_reserve_contact_method,
          displayOpacSetting: json.page.user.basic_tab.display_opac_setting,
          displayOther: json.page.user.basic_tab.display_other,
          displayAddress: json.page.user.basic_tab.display_address,
          displayMailAddress: json.page.user.basic_tab.display_mail_address
        },
        otherContactTab: {
          display: json.page.user.other_contact_tab.display
        },
        newArrivalNoticeTab: {
          display: json.page.user.new_arrival_notice_tab.display
        },
        oldNameTab: {
          display: json.page.user.old_name_tab.display
        },
        usageSituationTab: {
          displayRemindCountHistory:
            json.page.user.usage_situation_tab.display_remind_count_history,
          displayReserveCountHistory:
            json.page.user.usage_situation_tab.display_reserve_count_history
        },
        externalCollaborationTab: {
          display: json.page.user.external_collaboration_tab.display
        }
      },
      userSearch: {
        displayAdditionalSearch:
          json.page.user_search.display_additional_search,
        displayPhone: json.page.user_search.display_phone,
        displayAddress: json.page.user_search.display_address,
        displayMailAddress: json.page.user_search.display_mail_address
      },
      shelfTypeTempChange: {
        displayHandlingType:
          json.page.shelf_type_temp_change.display_handling_type
      },
      shelfTypeTempChangeFix: {
        displayHandlingType:
          json.page.shelf_type_temp_change_fix.display_handling_type
      },
      reserve: {
        displayReserveUserName: json.page.reserve.display_reserve_user_name,
        displayReserveMethod: json.page.reserve.display_reserve_method,
        displayForwardingBranch: json.page.reserve.display_forwarding_branch,
        displayForwardingPlace: json.page.reserve.display_forwarding_place,
        displayReserveContactMethod:
          json.page.reserve.display_reserve_contact_method
      },
      bibliographyDetail: {
        tooliDownload: {
          defaultMaterialFormat: {
            bibliographyReal:
              json.page.bibliography_detail.tooli_download
                .default_material_format.bibliography_real,
            bibliographyContent:
              json.page.bibliography_detail.tooli_download
                .default_material_format.bibliography_content
          }
        },
        displayReserveUserName:
          json.page.bibliography_detail.display_reserve_user_name,
        collectionOrderTab: {
          displayInterLibraryLoanCollectionRegister:
            json.page.bibliography_detail.collection_order_tab
              .display_inter_library_loan_collection_register,
          displayAccept:
            json.page.bibliography_detail.collection_order_tab.display_accept,
          displayRegister:
            json.page.bibliography_detail.collection_order_tab.display_register,
          displayOrder:
            json.page.bibliography_detail.collection_order_tab.display_order,
          displayRemovedSwitchDefaultValue:
            json.page.bibliography_detail.collection_order_tab
              .display_removed_switch_default_value,
          displaySetNumber:
            json.page.bibliography_detail.collection_order_tab
              .display_set_number
        },
        displayBibliographyIntegration:
          json.page.bibliography_detail.display_bibliography_integration,
        contentTab: {
          display: json.page.bibliography_detail.content_tab.display,
          requiredType: json.page.bibliography_detail.content_tab.required_type
        },
        bibliographyTab: {
          displayCopyRegister:
            json.page.bibliography_detail.bibliography_tab
              .display_copy_register,
          displayEdit:
            json.page.bibliography_detail.bibliography_tab.display_edit
        }
      },
      bibliographySearch: {
        displayInterLibraryLoanCollectionCreate:
          json.page.bibliography_search
            .display_inter_library_loan_collection_create,
        alone: {
          initialSelect: json.page.bibliography_search.alone.initial_select
        }
      },
      bibliographySearchResult: {
        displayInterLibraryLoanCollectionRegister:
          json.page.bibliography_search_result
            .display_inter_library_loan_collection_register,
        displayInterLibraryLoanCollectionCreate:
          json.page.bibliography_search_result
            .display_inter_library_loan_collection_create
      },
      systemCodeDialog: {
        initialFocusPosition:
          json.page.system_code_dialog.initial_focus_position
      },
      addressCodeDialog: {
        initialFocusPosition:
          json.page.address_code_dialog.initial_focus_position
      }
    },
    autoLib: {
      enabled: json.auto_lib.enabled,
      closedShelfTypes: json.auto_lib.closed_shelf_types,
      retrievalStationCode: json.auto_lib.retrieval_station_code
    },
    rewritecard: {
      enabled: json.rewritecard.enabled
    },
    mynumbercard: {
      enabled: json.mynumbercard.enabled
    },
    batch: {
      trcBellNewOrderRegistration: json.batch.trc_bell_new_order_registration,
      reminderList: {
        outputOptionDisabled: json.batch.reminder_list.output_option_disabled
      }
    },
    felica: {
      enabled: json.felica.enabled
    },
    env: {
      headerBarColor: json.env.header_bar_color
    }
  }
}
